import { useState, useRef, useEffect } from 'react';
import { ProcessingStatus } from '@/types/Document';

export interface DocumentTypeOption {
  id: string;
  name: string;
  icon: React.ReactNode;
}

// Document type options with icons
export const documentTypeOptions: DocumentTypeOption[] = [
  { 
    id: 'procedure', 
    name: 'Procedure',
    icon: (
      <svg className="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
      </svg>
    )
  },
  { 
    id: 'dsi', 
    name: 'DSI',
    icon: (
      <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"></path>
      </svg>
    )
  },
  { 
    id: 'formulaire', 
    name: 'Formulaire',
    icon: (
      <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
      </svg>
    )
  }
];

interface DocumentTypeDropdownProps {
  documentId: string;
  selectedType: string;
  processingStatus: ProcessingStatus | undefined;
  isProcessing: boolean;
  onTypeChange: (documentId: string, typeId: string) => Promise<void>;
}

export default function DocumentTypeDropdown({
  documentId,
  selectedType,
  processingStatus,
  isProcessing,
  onTypeChange
}: DocumentTypeDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<'up' | 'down'>('down');
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // Close dropdown when clicking outside the entire container (button + dropdown menu)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const isDisabled = isProcessing || 
    ['queued', 'processing', 'uploading', 'in_progress', 'preparing'].includes(processingStatus || '');

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (isOpen) {
      setIsOpen(false);
      return;
    }
    
    // Calculate dropdown position
    if (buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;
      const spaceBelow = windowHeight - buttonRect.bottom;
      setDropdownPosition(spaceBelow < 200 ? 'up' : 'down');
    }
    
    setIsOpen(true);
  };

  const handleSelect = async (typeId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setIsOpen(false);
    await onTypeChange(documentId, typeId);
  };

  const selectedOption = documentTypeOptions.find(option => option.id === selectedType) || documentTypeOptions[0];

  return (
    <div ref={containerRef} className="relative w-full">
      <button
        ref={buttonRef}
        type="button"
        onClick={handleToggle}
        className="relative w-10 h-10 flex items-center justify-center bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
        disabled={isDisabled}
      >
        {selectedOption.icon}
      </button>
      
      {isOpen && (
        <div 
          className={`absolute z-10 ${dropdownPosition === 'up' 
            ? 'bottom-full mb-1' 
            : 'top-full mt-1'} w-auto min-w-[150px] bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm`}
        >
          {documentTypeOptions.map((option) => (
            <div
              key={option.id}
              className={`${
                option.id === selectedType 
                  ? 'bg-gray-100 text-gray-900' 
                  : 'text-gray-900'
              } cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-indigo-600 hover:text-white`}
              onClick={(e) => handleSelect(option.id, e)}
            >
              <div className="flex items-center">
                <span className="text-gray-500 group-hover:text-white">
                  {option.icon}
                </span>
                <span className={`${
                  option.id === selectedType ? 'font-medium' : 'font-normal'
                } ml-2 truncate`}>
                  {option.name}
                </span>
              </div>

              {option.id === selectedType && (
                <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-indigo-600">
                  <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
